import { useEffect, useState } from "react";
import { ChevronDown, Loader2, Save, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SaveAsNewViewModal } from "./SaveAsNewViewModal";
import type { SavedViewRead } from "@/api/types";

export interface ViewActionsClusterProps {
  activeView: SavedViewRead | null;
  isDirty: boolean;
  canCreate: boolean;
  canShare: boolean;
  savingActive: boolean;
  previewItems?: string[];
  sortPreview?: string;
  onUpdateActive: () => Promise<void>;
  onResetActive: () => void;
  onCreateView: (data: {
    name: string;
    is_shared: boolean;
  }) => Promise<SavedViewRead>;
}

/**
 * Right-edge cluster of view actions. Extracted from ViewsBar in F6
 * so the new chips-bar row can host it without dragging the rest of
 * ViewsBar along. Surfaces ONLY when a named view is active AND
 * dirty.
 *
 *  Two dirty modes:
 *    - Named view I own  → [Discard] [Save ▾]      (split button)
 *    - Named view shared → [Discard] [Save as new] (no primary —
 *                          can't PATCH someone else's view)
 *
 *  Behavior is byte-for-byte the F3-bis ActionsCluster — only the
 *  module location changed.
 */
export function ViewActionsCluster({
  activeView,
  isDirty,
  canCreate,
  canShare,
  savingActive,
  previewItems,
  sortPreview,
  onUpdateActive,
  onResetActive,
  onCreateView,
}: ViewActionsClusterProps) {
  // Enter-only fade animation, same pattern as F2 chips. Local
  // mounted flag flips on the next paint; motion-safe: prefix on the
  // hidden state means users with reduced-motion preferences see no
  // initial-hidden flash.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Cluster only renders for a dirty named view. Raw state (no
  // active view) + clean named view = empty.
  if (!activeView || !isDirty) return null;

  // Shared-but-not-mine: primary Save is disabled (can't PATCH
  // someone else's view). The split-button collapses to a single
  // "Save as new" button.
  const isSharedView = !activeView.is_mine;

  const wrapperClass = cn(
    "inline-flex shrink-0 items-center gap-2 transition-all duration-200 motion-reduce:transition-none",
    mounted
      ? "translate-y-0 opacity-100"
      : "motion-safe:translate-y-1 motion-safe:opacity-0",
  );

  const discardButton = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onResetActive}
      className="text-slate-600 hover:text-slate-900"
    >
      <Undo2 className="mr-1.5 h-3.5 w-3.5" />
      Discard changes
    </Button>
  );

  // ─── Shared view I don't own — Save-as-new only (no primary) ───────
  if (isSharedView) {
    return (
      <div className={wrapperClass}>
        {discardButton}
        {canCreate && (
          <SaveAsNewViewModal
            canShare={canShare}
            previewItems={previewItems}
            sortPreview={sortPreview}
            triggerLabel="Save as new"
            defaultName={`${activeView.name} (copy)`}
            onSave={async (data) => {
              const created = await onCreateView(data);
              toast.success(`View "${created.name}" created`);
              return created;
            }}
          />
        )}
      </div>
    );
  }

  // ─── Named view I own — primary Save + chevron Save-as-new ─────────
  return (
    <div className={wrapperClass}>
      {discardButton}
      <div className="inline-flex shrink-0 items-center">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void onUpdateActive()}
          disabled={savingActive}
          className="rounded-r-none"
        >
          {savingActive ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="mr-1.5 h-3.5 w-3.5" />
          )}
          Save
        </Button>
        {canCreate && (
          <SaveAsNewViewModal
            canShare={canShare}
            previewItems={previewItems}
            sortPreview={sortPreview}
            triggerLabel=""
            triggerIcon={<ChevronDown className="h-3.5 w-3.5" />}
            triggerClassName="rounded-l-none border-l-0 px-2"
            defaultName={`${activeView.name} (copy)`}
            onSave={async (data) => {
              const created = await onCreateView(data);
              toast.success(`View "${created.name}" created`);
              return created;
            }}
          />
        )}
      </div>
    </div>
  );
}
